import React from 'react';
import './TermsPage.css';

export default function TermsPage() {
  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.close();
    }
  };

  return (
    <div className="terms-page">
      <div className="terms-header">
        <div className="terms-header-inner">
          <img src="/logo.png" alt="DealNexa" className="terms-logo" />
          <button className="terms-back-btn" onClick={handleBack}>← Close</button>
        </div>
      </div>

      <div className="terms-container">
        <div className="terms-doc">

          <div className="terms-title-block">
            <h1>DEALNEXA</h1>
            <h2>SUBSCRIBER AGREEMENT</h2>
            <div className="terms-meta">
              <p><strong>Effective Date:</strong> March 31, 2026</p>
              <p><strong>Version:</strong> 1.1</p>
              <p className="terms-legal-notice">This is a legally binding document. Please read carefully before agreeing.</p>
            </div>
          </div>

          <nav className="terms-toc">
            <h3>TABLE OF CONTENTS</h3>
            <ol>
              <li><a href="#section-1">Definitions</a></li>
              <li><a href="#section-2">Subscription Services</a></li>
              <li><a href="#section-3">Subscriber Responsibilities</a></li>
              <li><a href="#section-4">Fees and Payment</a></li>
              <li><a href="#section-5">Data and Security</a></li>
              <li><a href="#section-6">Intellectual Property</a></li>
              <li><a href="#section-7">Term and Termination</a></li>
              <li><a href="#section-8">Warranties and Disclaimers</a></li>
              <li><a href="#section-9">Limitation of Liability</a></li>
              <li><a href="#section-10">Indemnification</a></li>
              <li><a href="#section-11">General Provisions</a></li>
            </ol>
          </nav>

          <section id="section-1">
            <h2>1. DEFINITIONS</h2>
            <dl>
              <dt>"DealNexa" or "Platform"</dt>
              <dd>refers to the deal management and workflow automation software provided by [Your Company Legal Name].</dd>
              <dt>"Subscriber" or "Customer"</dt>
              <dd>means the individual or entity accessing the Platform.</dd>
              <dt>"User"</dt>
              <dd>means any individual authorized by Subscriber to access the Platform.</dd>
              <dt>"Deal Data"</dt>
              <dd>means all information, documents, and data submitted to or created within the Platform.</dd>
              <dt>"Diligence Fee"</dt>
              <dd>means the $50,000 fee incurred when a deal is moved to Diligence stage.</dd>
              <dt>"Free Access"</dt>
              <dd>means access granted to the Platform at no charge, including promotional, trial, or complimentary access explicitly designated as "Free" by DealNexa.</dd>
              <dt>"Paid Subscriber"</dt>
              <dd>means any Subscriber paying subscription fees or subject to Diligence Fees.</dd>
              <dt>"Subscription Term"</dt>
              <dd>means the initial period and any renewal periods.</dd>
            </dl>
          </section>

          <section id="section-2">
            <h2>2. SUBSCRIPTION SERVICES</h2>
            <h3>2.1 Grant of Access</h3>
            <p>Subject to payment of applicable fees (unless granted Free Access), DealNexa grants Subscriber a non-exclusive, non-transferable, limited license to access and use the Platform during the Subscription Term solely for internal business purposes.</p>

            <h3>2.2 Platform Description</h3>
            <p>DealNexa provides:</p>
            <ul>
              <li>Deal pipeline management and tracking</li>
              <li>Document storage and organization</li>
              <li>Workflow automation tools</li>
              <li>Team collaboration features</li>
              <li>Analytics and reporting dashboards</li>
              <li>API access (if applicable to plan)</li>
            </ul>

            <h3>2.3 Authorized Usage</h3>
            <p>Subscriber may create user accounts up to the limit specified in their selected plan or Free Access tier. Each User must have unique login credentials. Sharing credentials is strictly prohibited.</p>

            <h3>2.4 Free Access Terms</h3>
            <p>DealNexa may grant Free Access at its sole discretion. Free Access:</p>
            <ul>
              <li>May be time-limited or feature-limited</li>
              <li>Exempts Subscriber from Diligence Fees as detailed in Section 4.8</li>
              <li>May be revoked or converted to Paid Subscriber status with 30 days notice</li>
              <li>Does not include SLAs or guaranteed support response times</li>
            </ul>
          </section>

          <section id="section-3">
            <h2>3. SUBSCRIBER RESPONSIBILITIES</h2>
            <h3>3.1 Account Security</h3>
            <p>Subscriber is responsible for:</p>
            <ul>
              <li>Maintaining confidentiality of all login credentials</li>
              <li>All activity occurring under their accounts</li>
              <li>Notifying DealNexa within 24 hours of any unauthorized access</li>
              <li>Ensuring Users comply with this Agreement</li>
            </ul>

            <h3>3.2 Acceptable Use</h3>
            <p>Subscriber agrees NOT to:</p>
            <ul>
              <li>Use the Platform for unlawful purposes</li>
              <li>Upload malicious code or viruses</li>
              <li>Reverse engineer or attempt to access source code</li>
              <li>Resell, sublicense, or commercially exploit the Platform</li>
              <li>Use automated scripts or scraping tools</li>
              <li>Upload false, misleading, or fraudulent deal information</li>
              <li>Move deals to Diligence stage solely to trigger fees (if applicable)</li>
            </ul>

            <h3>3.3 Data Compliance</h3>
            <p>Subscriber represents that all Deal Data uploaded complies with applicable laws and that Subscriber has obtained necessary rights and consents to share such data.</p>
          </section>

          <section id="section-4">
            <h2>4. FEES AND PAYMENT</h2>
            <h3>4.1 Subscription Charges</h3>
            <p>Fees are based on the plan selected (Starter, Growth, or Enterprise) and billed:</p>
            <ul>
              <li>Monthly plans: In advance, on the subscription date</li>
              <li>Annual plans: In full at the start of the term</li>
            </ul>

            <h3>4.2 Success-Based Fees</h3>
            <p><strong>Diligence Stage Fee:</strong> When a deal is moved to the "Diligence" stage within the Platform, Subscriber agrees to pay a $50,000 diligence fee ("Diligence Fee") per deal.</p>
            <ul>
              <li><strong>Trigger:</strong> Status change to "Diligence" or equivalent stage designation</li>
              <li><strong>Invoice Timing:</strong> Fee is invoiced immediately upon stage change</li>
              <li><strong>Payment Terms:</strong> Net 30 days from invoice date</li>
              <li><strong>Deal Definition:</strong> Each unique transaction, asset, or opportunity tracked as a separate deal record</li>
            </ul>

            <h3>4.3 Payment Terms</h3>
            <table className="terms-table">
              <thead>
                <tr><th>Fee Type</th><th>Payment Terms</th></tr>
              </thead>
              <tbody>
                <tr><td>Subscription</td><td>Automatic renewal; valid payment method required</td></tr>
                <tr><td>Diligence Fee</td><td>Net 30 days; wire transfer or ACH preferred</td></tr>
                <tr><td>Failed Payments</td><td>5-day grace period, then suspension of access</td></tr>
              </tbody>
            </table>
            <ul>
              <li>Automatic renewal unless cancelled 30 days prior to renewal date</li>
              <li>Failed subscription payments: 5-day grace period, then suspension of Platform access</li>
              <li>Outstanding Diligence Fees: May result in deal lock or account restrictions until paid</li>
              <li>All fees are non-refundable except as required by law</li>
            </ul>

            <h3>4.4 Plan Changes</h3>
            <ul>
              <li><strong>Upgrades:</strong> Take effect immediately; prorated charges apply</li>
              <li><strong>Downgrades:</strong> Take effect at next renewal date; no refunds for partial months</li>
              <li><strong>Diligence Fees:</strong> Independent of subscription plan; apply to all active deals moved to diligence regardless of plan tier (unless Free Access applies per Section 4.8)</li>
            </ul>

            <h3>4.5 Fee Disputes</h3>
            <p>Subscriber must dispute any fee within 15 days of invoice date. Undisputed portions must be paid when due. DealNexa will promptly investigate any good-faith disputes.</p>

            <h3>4.6 Diligence Fee Acknowledgment</h3>
            <p>Subscriber specifically acknowledges and agrees that:</p>
            <ul>
              <li><strong>Stage Changes Are Irreversible:</strong> Moving a deal to Diligence stage triggers the fee immediately and cannot be undone to avoid charges</li>
              <li><strong>User Authority:</strong> Only users with "Admin" or "Deal Lead" permissions can move deals to Diligence</li>
              <li><strong>Fee Notification:</strong> Platform will display a prominent confirmation modal before allowing stage change: "Moving this deal to Diligence will incur a $50,000 fee. Confirm to proceed."</li>
              <li><strong>Multiple Deals:</strong> Each deal moved to Diligence incurs a separate $50,000 fee</li>
              <li><strong>No Refunds:</strong> Diligence Fees are non-refundable once the stage change is confirmed, even if the deal later falls through or returns to earlier stages</li>
            </ul>

            <h3>4.7 Collections</h3>
            <p>For overdue Diligence Fees exceeding 60 days:</p>
            <ul>
              <li>DealNexa may restrict ability to move additional deals to Diligence</li>
              <li>Access to diligence-stage deals may be limited to "view-only" mode</li>
              <li>DealNexa reserves the right to engage collections services</li>
              <li>Subscriber liable for all reasonable collection costs and attorney fees</li>
            </ul>

            <h3>4.8 Free Access Exemption</h3>
            <p>Notwithstanding any other provision in this Agreement:</p>
            <ul>
              <li><strong>No Diligence Fees:</strong> Subscribers with Free Access are exempt from all Diligence Fees described in Section 4.2</li>
              <li><strong>No Subscription Fees:</strong> Subscribers with Free Access owe no subscription fees</li>
              <li><strong>Status Verification:</strong> Free Access status is determined by DealNexa's internal records and displayed in the Subscriber's account settings</li>
            </ul>
            <p>Upon conversion from Free Access to Paid Subscriber status:</p>
            <ul>
              <li>Diligence Fees apply only to deals moved to Diligence after the conversion date</li>
              <li>Deals already in Diligence stage during Free Access remain exempt from fees</li>
              <li>Subscription fees begin on the conversion date</li>
            </ul>
            <p>Free Access does not exempt Subscriber from:</p>
            <ul>
              <li>Acceptable Use obligations (Section 3.2)</li>
              <li>Data compliance responsibilities (Section 3.3)</li>
              <li>Indemnification obligations (Section 10)</li>
              <li>Liability for damages caused by misuse or violation of this Agreement</li>
            </ul>
          </section>

          <section id="section-5">
            <h2>5. DATA AND SECURITY</h2>
            <h3>5.1 Data Ownership</h3>
            <p>Subscriber retains all ownership rights to Deal Data. DealNexa claims no ownership over subscriber content.</p>

            <h3>5.2 Data Protection</h3>
            <p>DealNexa implements industry-standard security measures including:</p>
            <ul>
              <li>Encryption in transit (TLS 1.2+) and at rest</li>
              <li>SOC 2 Type II compliant infrastructure</li>
              <li>Regular security audits and penetration testing</li>
              <li>Role-based access controls</li>
            </ul>

            <h3>5.3 Data Processing</h3>
            <p>DealNexa processes Deal Data solely to provide the Platform services. See our Data Processing Addendum and Privacy Policy for detailed data handling procedures.</p>

            <h3>5.4 Data Retention for Free Access</h3>
            <p>Upon termination of Free Access:</p>
            <ul>
              <li>Deal Data retained for 30 days</li>
              <li>Export functionality available during this period</li>
              <li>After 30 days, data deleted unless Subscriber converts to Paid Subscriber</li>
            </ul>
          </section>

          <section id="section-6">
            <h2>6. INTELLECTUAL PROPERTY</h2>
            <p>All rights, title, and interest in the Platform, including software, algorithms, designs, and trademarks, remain the exclusive property of DealNexa. This Agreement grants no ownership rights.</p>
          </section>

          <section id="section-7">
            <h2>7. TERM AND TERMINATION</h2>
            <h3>7.1 Initial Term &amp; Renewal</h3>
            <p>Paid Subscriptions automatically renew for successive periods equal to the initial term unless cancelled. Free Access continues until revoked or converted.</p>

            <h3>7.2 Termination for Convenience</h3>
            <ul>
              <li><strong>Paid Subscribers:</strong> May cancel anytime via account settings or by emailing support@deal-nexa.com. Access continues until the end of the paid period.</li>
              <li><strong>Free Access:</strong> May be terminated by either party with 30 days notice. DealNexa may terminate immediately for violation of Acceptable Use terms.</li>
            </ul>

            <h3>7.3 Termination for Cause</h3>
            <p>Either party may terminate immediately for material breach if uncured within 30 days of notice.</p>

            <h3>7.4 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul>
              <li>Access to Platform ceases immediately</li>
              <li>DealNexa will retain Deal Data for 30 days, then delete</li>
              <li>Subscriber may export data during the 30-day period</li>
              <li><strong>Paid Subscribers only:</strong> All outstanding Diligence Fees and subscription fees become immediately due</li>
              <li><strong>Free Access:</strong> No fees owed; all deals remain exempt from Diligence Fees</li>
            </ul>
          </section>

          <section id="section-8">
            <h2>8. WARRANTIES AND DISCLAIMERS</h2>
            <h3>8.1 Platform Warranty</h3>
            <p>DealNexa warrants that the Platform will materially conform to documentation and be provided with reasonable skill and care.</p>

            <h3>8.2 Free Access Disclaimer</h3>
            <p className="terms-caps">FOR FREE ACCESS USERS: THE PLATFORM IS PROVIDED "AS IS" WITHOUT ANY WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR PURPOSE, OR NON-INFRINGEMENT.</p>

            <h3>8.3 General Disclaimer</h3>
            <p className="terms-caps">EXCEPT AS EXPRESSLY STATED, THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. DEALNEXA DOES NOT WARRANT UNINTERRUPTED SERVICE OR THAT THE PLATFORM WILL BE ERROR-FREE.</p>
          </section>

          <section id="section-9">
            <h2>9. LIMITATION OF LIABILITY</h2>
            <p className="terms-caps">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul className="terms-caps-list">
              <li>DEALNEXA'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY SUBSCRIBER IN THE 12 MONTHS PRECEDING THE CLAIM</li>
              <li>FOR FREE ACCESS USERS: DEALNEXA'S TOTAL LIABILITY SHALL NOT EXCEED $100</li>
              <li>NO LIABILITY FOR INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES</li>
              <li>NO LIABILITY FOR LOST PROFITS, DATA LOSS, OR BUSINESS INTERRUPTION</li>
              <li>THIS LIMITATION DOES NOT APPLY TO OUTSTANDING DILIGENCE FEES FOR PAID SUBSCRIBERS, WHICH REMAIN DUE REGARDLESS OF LIABILITY CAP</li>
            </ul>
          </section>

          <section id="section-10">
            <h2>10. INDEMNIFICATION</h2>
            <p>Subscriber agrees to indemnify DealNexa against claims arising from:</p>
            <ul>
              <li>Subscriber's misuse of the Platform</li>
              <li>Deal Data uploaded by Subscriber</li>
              <li>Violation of this Agreement or applicable laws</li>
            </ul>
            <p>Applies regardless of Free Access or Paid Subscriber status.</p>
          </section>

          <section id="section-11">
            <h2>11. GENERAL PROVISIONS</h2>
            <h3>11.1 Governing Law</h3>
            <p>This Agreement is governed by the laws of [Your State/Country], without regard to conflict of laws principles.</p>

            <h3>11.2 Dispute Resolution</h3>
            <p>Any disputes shall first be attempted to resolve through good faith negotiation, then binding arbitration in [Your City].</p>

            <h3>11.3 Modifications</h3>
            <p>DealNexa may modify this Agreement with 30 days' notice. Continued use constitutes acceptance. Material changes to fee structures require explicit re-acceptance.</p>

            <h3>11.4 Severability</h3>
            <p>If any provision is found unenforceable, the remainder of the Agreement remains in effect.</p>

            <h3>11.5 Entire Agreement</h3>
            <p>This Agreement constitutes the entire agreement between parties regarding the Platform.</p>

            <h3>11.6 Free Access Documentation</h3>
            <p>DealNexa maintains records of Free Access grants, including start date, scope, and any limitations. Subscriber may request confirmation of Free Access status at any time.</p>
          </section>

          <section className="terms-signature">
            <h2>SIGNATURE BLOCK</h2>
            <p className="terms-caps">BY CLICKING "I AGREE" OR ACCESSING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THIS AGREEMENT, INCLUDING:</p>
            <ul className="terms-caps-list">
              <li>THE $50,000 DILIGENCE FEE STRUCTURE DESCRIBED IN SECTION 4.2 (if applicable to your account type)</li>
              <li>THE FREE ACCESS EXEMPTION DESCRIBED IN SECTION 4.8 (if applicable to your account type)</li>
              <li>THE LIMITATION OF LIABILITY PROVISIONS IN SECTION 9</li>
            </ul>
            <p className="terms-last-updated">Last Updated: March 31, 2026</p>
          </section>

        </div>
      </div>
    </div>
  );
}
